/**
 * ProductTourFormatterService
 * 
 * Tour product verilerini formatlayan utility class
 * OOP prensiplerine uygun, static method'lar ile merkezi yapı
 */

import { TourDuration } from './ProductTourDataService';

export class ProductTourFormatterService {
    /**
     * Tour duration'ı formatla (örn: "13 Days - 12 Nights")
     */
    static formatTourDuration(duration: TourDuration | null, language: string = 'en'): string {
        if (!duration) return '';

        const translations: Record<string, { day: string; night: string; separator: string }> = {
            en: { day: 'Day', night: 'Night', separator: ' - ' },
            tr: { day: 'Gün', night: 'Gece', separator: ' - ' },
        };

        const t = translations[language] || translations.en;
        const parts: string[] = [];

        if (duration.day > 0) {
            parts.push(`${duration.day} ${duration.day === 1 ? t.day : t.day + 's'}`);
        }
        if (duration.night > 0) {
            parts.push(`${duration.night} ${duration.night === 1 ? t.night : t.night + 's'}`);
        }

        return parts.join(t.separator);
    }

    /**
     * Group size'ı formatla (örn: "2-5 Kişi")
     */
    static formatGroupSize(
        minSize: number | null,
        maxSize: number | null,
        language: string = 'en'
    ): string {
        if (!minSize && !maxSize) return '';

        const translations: Record<string, string> = {
            en: 'Pax',
            tr: 'Kişi',
        };

        const t = translations[language] || translations.en;

        if (minSize && maxSize) {
            if (minSize === maxSize) {
                return `${minSize} ${t}`;
            }
            return `${minSize}-${maxSize} ${t}`;
        }

        if (minSize) {
            return `${minSize}+ ${t}`;
        }

        if (maxSize) {
            return `Up to ${maxSize} ${t}`;
        }

        return '';
    }

    /**
     * Season'ı formatla
     */
    static formatSeason(season: string | null, language: string = 'en'): string {
        if (!season) return '';

        const translations: Record<string, Record<string, string>> = {
            en: {
                summer: 'Summer',
                winter: 'Winter',
                spring: 'Spring',
                autumn: 'Autumn',
                all_season: 'All Season',
            },
            tr: {
                summer: 'Yaz',
                winter: 'Kış',
                spring: 'İlkbahar',
                autumn: 'Sonbahar',
                all_season: 'Tüm Sezon',
            },
        };

        const t = translations[language] || translations.en;
        return t[season] || season;
    }

    /**
     * Transfer tiplerini formatla
     */
    static formatTransfer(transfer: string[], language: string = 'en'): string {
        if (!transfer || transfer.length === 0) return '';

        const translations: Record<string, Record<string, string>> = {
            en: {
                vehicle: 'Vehicle',
                airplane: 'Airplane',
                ferry: 'Ferry',
                train: 'Train',
                ship: 'Ship',
                yacht: 'Yacht',
                // Backward compatibility
                car: 'Vehicle',
                bus: 'Vehicle',
                minibus: 'Vehicle',
                private_vip_transfer: 'Vehicle',
            },
            tr: {
                vehicle: 'Araç',
                airplane: 'Uçak',
                ferry: 'Feribot',
                train: 'Tren',
                ship: 'Gemi',
                yacht: 'Yat',
                // Backward compatibility
                car: 'Araç',
                bus: 'Araç',
                minibus: 'Araç',
                private_vip_transfer: 'Araç',
            },
        };

        const t = translations[language] || translations.en;
        return transfer.map(tf => t[tf] || tf).join(', ');
    }

    /**
     * Tour type'ı formatla
     */
    static formatTourType(tourType: string | null, language: string = 'en'): string {
        if (!tourType) return '';

        const raw = tourType.trim().toLowerCase().replace(/\s+/g, '_');
        const translations: Record<string, Record<string, string>> = {
            en: {
                private: 'Private Tour',
                group: 'Group Tour',
                self_guided: 'Self-Guided Tour',
                daily: 'Daily tour',
                'daily-tour': 'Daily tour',
                'daily_tour': 'Daily tour',
            },
            tr: {
                private: 'Özel Tur',
                group: 'Grup Turu',
                self_guided: 'Rehbersiz Tur',
                daily: 'Günlük tur',
                'daily-tour': 'Günlük tur',
                'daily_tour': 'Günlük tur',
            },
        };

        const t = translations[language] || translations.en;
        return t[raw] ?? t[tourType] ?? tourType;
    }

    /**
     * Fiyatı formatla (currency symbol ile)
     */
    static formatPrice(price: number, currencySymbol: string = '€', decimals: number = 2): string {
        if (typeof price !== 'number' || isNaN(price)) return `${currencySymbol}0.00`;
        return `${currencySymbol}${price.toFixed(decimals)}`;
    }

    /**
     * Lokasyon metnini özet/kısa formata çevir (Features bölümü için)
     * Tam adres → "Şehir, Ülke" formatı
     * Örn: "Hilal, Kayseri Havalimanı, ..., Kocasinan/Kayseri, Türkiye" → "Kayseri, Türkiye"
     * Örn: "Arrival Van" → "Van", "Rize or Trabzon" → "Rize or Trabzon"
     */
    static simplifyLocationDisplay(location: string | null): string | null {
        if (!location || !location.trim()) return null;
        const s = location.trim();

        const parts = s.split(',').map((p) => p.trim());
        // Virgül yok veya tek virgül var → olduğu gibi dön
        if (parts.length <= 2) return s;
        // 3+ parça varsa: sondan 2 parçayı al (şehir/ilçe, ülke)
        return parts.slice(-2).join(', ');
    }

    /**
     * Rating'i formatla (örn: "4.8 (513 Reviews)")
     */
    static formatRating(rating: number, reviewCount: number, language: string = 'en'): string {
        const translations: Record<string, string> = {
            en: 'Reviews',
            tr: 'Yorum',
        };

        const t = translations[language] || translations.en;
        const reviewText = reviewCount === 1 ? t : t + 's';
        return `${rating.toFixed(1)} (${reviewCount} ${reviewText})`;
    }
}

